import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'expo-router';
import { useCartContinuePricing } from 'features/cart/hooks/use-cart-continue/use-cart-continue-pricing';
import { useCartValidation } from 'features/cart/hooks/use-cart-validation';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { countCartItems } from 'features/cart/utils';
import { useStoreContext } from 'features/store';

import { routes } from 'navigation/routing';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';

import { useCartState } from '../../cart-provider';

export const useCartContinue = () => {
  const [, setCartState] = useCartState();
  const { cartEntries, removeItemFromMinimalCart, emptyMinimalCart } = useMinimalCart();
  const { store } = useStoreContext();
  const { deliveryAddress } = useOrderContext();
  const { isDelivery } = useServiceModeContext();
  const { navigate } = useRouter();
  const dispatch = useAppDispatch();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const [showInvalidItemsModal, setShowInvalidItemsModal] = useState(false);
  const [showDeliveryMinimumSubtotalModal, setShowDeliveryMinimumSubtotalModal] = useState(false);
  const [showSelectionRequiredModal, setShowSelectionRequiredModal] = useState(false);

  const appliedOffersByCartId = useMemo(
    () => new Map(appliedOffers.map(offer => [offer.cartId, offer])),
    [appliedOffers]
  );

  const {
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    hasInvalidEntries,
    hasBlockingOfferErrors,
    isDeliverySubtotalValid,
    isCartTotalValid,
    isCartTotalZeroWithoutReward,
    deliveryMinimumSubtotalCents,
    isMenuReady,
  } = useCartValidation({ cartEntries });

  const hasCartItems = useMemo(() => countCartItems(cartEntries) > 0, [cartEntries]);
  const hasStore = Boolean(store?.number);
  const hasDeliveryAddress = !isDelivery || Boolean(deliveryAddress);

  const handlePricingSuccess = useCallback(() => {
    navigate(routes.cartCheckout);
  }, [navigate]);

  const { startPricing, resetPricing, isPricingLoading, pricingError } = useCartContinuePricing({
    cartEntries,
    onSuccess: handlePricingSuccess,
  });

  const hasContinuePrerequisites =
    hasCartItems &&
    hasStore &&
    hasDeliveryAddress &&
    isMenuReady &&
    !isPricingLoading &&
    !pricingError;
  const hasRetryPrerequisites =
    hasCartItems && hasStore && hasDeliveryAddress && isMenuReady && !isPricingLoading;

  const canAttemptPricing =
    !hasBlockingOfferErrors &&
    !hasInvalidEntries &&
    isDeliverySubtotalValid &&
    isCartTotalValid &&
    !isCartTotalZeroWithoutReward;
  const canContinue = hasContinuePrerequisites && canAttemptPricing;
  const canRetryPricing = hasRetryPrerequisites && canAttemptPricing;

  useEffect(() => {
    setCartState(prev =>
      prev.isContinueLoading === isPricingLoading
        ? prev
        : { ...prev, isContinueLoading: isPricingLoading }
    );

    return () => {
      setCartState(prev => (prev.isContinueLoading ? { ...prev, isContinueLoading: false } : prev));
    };
  }, [isPricingLoading, setCartState]);

  const handleContinue = useCallback(() => {
    if (!hasContinuePrerequisites) {
      return;
    }

    if (isCartTotalZeroWithoutReward) {
      setShowSelectionRequiredModal(true);
      return;
    }

    if (!isDeliverySubtotalValid) {
      setShowDeliveryMinimumSubtotalModal(true);
      return;
    }

    if (hasInvalidEntries) {
      setShowInvalidItemsModal(true);
      return;
    }

    if (hasBlockingOfferErrors) {
      return;
    }

    if (!isCartTotalValid) {
      // Fallback for other invalid totals (e.g., negative subtotal).
      setShowSelectionRequiredModal(true);
      return;
    }

    startPricing();
  }, [
    hasInvalidEntries,
    hasBlockingOfferErrors,
    hasContinuePrerequisites,
    isCartTotalValid,
    isCartTotalZeroWithoutReward,
    isDeliverySubtotalValid,
    startPricing,
  ]);

  const handleOpenInvalidItemsModal = useCallback(() => {
    // Stale/unavailable cart entries should be surfaced as soon as the user opens cart.
    if (isMenuReady && hasInvalidEntries && !showInvalidItemsModal) {
      setShowInvalidItemsModal(true);
    }
  }, [hasInvalidEntries, isMenuReady, showInvalidItemsModal]);

  const handleInvalidItemsConfirm = useCallback(() => {
    invalidCartEntries.forEach(entry => removeItemFromMinimalCart(entry));
    invalidOfferEntries.forEach(entry => {
      removeItemFromMinimalCart(entry);
      const appliedOffer = appliedOffersByCartId.get(entry.cartId ?? '');
      if (appliedOffer) {
        dispatch(actions.loyalty.removeAppliedOffer(appliedOffer));
      }
    });
    invalidRewardEntries.forEach(entry => removeItemFromMinimalCart(entry));
    setShowInvalidItemsModal(false);
  }, [
    appliedOffersByCartId,
    dispatch,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    removeItemFromMinimalCart,
  ]);

  const handleTryAgain = useCallback(() => {
    resetPricing();

    if (!canRetryPricing) {
      return;
    }

    startPricing();
  }, [canRetryPricing, resetPricing, startPricing]);

  const handleTryAnotherStore = useCallback(() => {
    emptyMinimalCart();
    navigate(routes.storeLocator);
  }, [emptyMinimalCart, navigate]);

  return {
    cartEntries,
    handleContinue,
    handleOpenInvalidItemsModal,
    handleInvalidItemsConfirm,
    handlePricingErrorClose: resetPricing,
    handleDeliveryMinimumSubtotalClose: () => setShowDeliveryMinimumSubtotalModal(false),
    handleSelectionRequiredClose: () => setShowSelectionRequiredModal(false),
    handleTryAgain,
    handleTryAnotherStore,
    deliveryMinimumSubtotalCents,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    isContinueDisabled: !canContinue,
    isPricingLoading,
    pricingError,
    showDeliveryMinimumSubtotalModal,
    showInvalidItemsModal,
    showSelectionRequiredModal,
  } as const;
};
